import { Stack, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import React from 'react';
import { useSelector } from 'react-redux';

import { makeSelectCommercialField } from 'Containers/App/selectors';
import { TextField } from 'Containers/App/types';
import { makeGetFsaMedicalPlan } from 'Containers/OverviewPage/selectors';
import { ContentfulCustomContentBlock } from 'ContentfulDefaults/types/_shared';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useFederalLimitsContext } from 'Contexts/federalLimitsContext';
import { useTextContext } from 'Contexts/textContext';
import { Button, H3, Icon, Paragraph } from 'DesignLibrary/atoms';
import { Accordion } from 'DesignLibrary/atoms/Accordion';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { EducationalDrawerInfoTypes, SAVINGS_ACCOUNT_TYPES, SupplementalPlan } from 'Types/entities';
import { formatDate, toDollars } from 'Utils/helpers';
import { makeYearReplacement } from 'Utils/textReplacement';
import { useFederalLimit } from 'Utils/useFederalLimit';

import { FAQContainer, FAQLinkWrapper, TableWrapper } from './styled';

interface FAQProps {
  infoType: EducationalDrawerInfoTypes;
  showLimitedProviderHraEducation?: boolean;
  hdhpMinDeductible?: number | null;
  employerHraContribution?: number | null;
}

// Jake's Comments:
// Look into Chakra for their Table components
// Should make styling the tables much easier

// Accordion component can get updated to match the design

// Fix ES Disable line at top of file

const FSA_FAQ_HARDCODED_YEAR = '2026';

// The unused-funds FAQ block names two adjacent years ("… 2026 funds … to 2027"); a
// single-year replace would corrupt it, so it must never be added to this set.
const SINGLE_YEAR_FSA_FAQ_BLOCKS = new Set([
  'fsa-faq-contribution-default',
  'fsa-faq-contribution-federal-limit',
  'fsa-faq-contribution-custom-max-only',
  'fsa-faq-contribution-custom-min-max',
]);

export const FAQ = ({
  infoType,
  showLimitedProviderHraEducation = false,
  hdhpMinDeductible,
  employerHraContribution,
}: FAQProps) => {
  const { retrieveContentfulData } = useTextContext();
  const jvpMedicalFsaLimit = useFederalLimit('medical_fsa');
  const { is_federal_limits_from_jvp_enabled: isFederalLimitsEnabled } = useFeatureFlagContext();
  const selectedPlanYear = useFederalLimitsContext()?.year;

  const selectedHealthPlan = useSelector(makeSelectCommercialField('selectedHealthPlan'));
  const fsaMedicalPlan = useSelector(makeGetFsaMedicalPlan());
  const isPlanSelected = !!(selectedHealthPlan as Record<string, unknown>)?.plan;
  const effectiveFsaPlan: SupplementalPlan | undefined = (() => {
    if (infoType !== SAVINGS_ACCOUNT_TYPES.FSA || !isPlanSelected) return undefined;
    const planDetails = (selectedHealthPlan as Record<string, unknown>)?.plan_details as Record<
      string,
      unknown
    >;
    const flexibleSavingsAccount = planDetails?.flexible_savings_account as
      | Record<string, unknown>
      | undefined;
    return flexibleSavingsAccount?.supplemental_plan as SupplementalPlan | undefined;
  })();
  const activeFsaPlan = effectiveFsaPlan ?? fsaMedicalPlan ?? undefined;
  const fsaSpecific = activeFsaPlan?.fsa_specific;

  const faqContentItemsPath = `health_section.secondary_content.${infoType}_modal.faq_content`;

  const faqContentItems = retrieveContentfulData<ContentfulCustomContentBlock[]>(faqContentItemsPath);

  const findFsaItem = (plan: SupplementalPlan | undefined, key: string): string | undefined => {
    const item = plan?.benefits?.items?.find((i) => {
      const k = typeof i.item === 'string' ? i.item : (i.item as { data: string }).data;
      return k === key;
    });
    const data = item?.description?.data;
    return data !== undefined ? String(data) : undefined;
  };

  const usesFederalLimit = findFsaItem(activeFsaPlan, 'uses_federal_limit') === 'true';
  const minContribution = findFsaItem(activeFsaPlan, 'minimum_contribution');
  const maxContribution = findFsaItem(activeFsaPlan, 'maximum_contribution');
  const hasRollover = findFsaItem(activeFsaPlan, 'has_rollover') === 'true';
  const fsaMax = toDollars(maxContribution);
  const fsaMin = toDollars(minContribution);
  const getFsaDateStr = (field: { data: string; rule: string; type: string } | string): string =>
    typeof field === 'string' ? field : field.data;
  const fsaIncurDate = fsaSpecific?.grace_period
    ? formatDate(getFsaDateStr(fsaSpecific.grace_period), 'en-US')
    : '';
  const fsaSubmitDate = fsaSpecific?.rollover_end_date
    ? formatDate(getFsaDateStr(fsaSpecific.rollover_end_date), 'en-US')
    : '';
  const fsaRolloverAmt = toDollars(fsaSpecific?.rollover_amount_limit?.data);
  const hasFsaDates = !!(fsaSpecific?.grace_period && fsaSpecific?.rollover_end_date);

  const visibleIndexes = faqContentItems.flatMap((item, idx) => {
    switch (item.block_id) {
      case 'hra-educational-modal-faq-garner-hra-and-hsa-combo':
        return showLimitedProviderHraEducation ? [idx] : [];
      case 'fsa-faq-contribution-default':
        return activeFsaPlan ? [] : [idx];
      case 'fsa-faq-contribution-federal-limit':
        return activeFsaPlan && usesFederalLimit ? [idx] : [];
      case 'fsa-faq-contribution-custom-max-only':
        return activeFsaPlan && !usesFederalLimit && !Number(minContribution) ? [idx] : [];
      case 'fsa-faq-contribution-custom-min-max':
        return activeFsaPlan && !usesFederalLimit && Number(minContribution) > 0 ? [idx] : [];
      case 'fsa-faq-unused-funds-default':
        return !activeFsaPlan || !hasFsaDates ? [idx] : [];
      case 'fsa-faq-unused-funds-rollover':
        return activeFsaPlan && hasRollover && hasFsaDates ? [idx] : [];
      case 'fsa-faq-unused-funds-no-rollover':
        return activeFsaPlan && !hasRollover && hasFsaDates ? [idx] : [];
      default:
        return [idx];
    }
  });

  const faqItems = visibleIndexes.map((idx) => {
    const faqContentItem = faqContentItems[idx];

    let vars: string[] | undefined;

    if (faqContentItem.block_id === 'hra-educational-modal-faq-garner-hra-and-hsa-combo') {
      vars = [toDollars(hdhpMinDeductible), toDollars(employerHraContribution)];
    } else if (faqContentItem.block_id === 'fsa-faq-contribution-federal-limit') {
      vars = [
        toDollars(
          jvpMedicalFsaLimit ??
            retrieveContentfulData<number>('benefits_section_medical_fsa.federal_contribution_limit'),
        ),
      ];
    } else if (faqContentItem.block_id === 'fsa-faq-contribution-custom-max-only') {
      vars = [fsaMax];
    } else if (faqContentItem.block_id === 'fsa-faq-contribution-custom-min-max') {
      vars = [fsaMin, fsaMax];
    } else if (faqContentItem.block_id === 'fsa-faq-unused-funds-rollover') {
      vars = [fsaRolloverAmt, fsaIncurDate, fsaSubmitDate];
    } else if (faqContentItem.block_id === 'fsa-faq-unused-funds-no-rollover') {
      vars = [fsaIncurDate, fsaSubmitDate];
    }

    const yearReplacement =
      isFederalLimitsEnabled && SINGLE_YEAR_FSA_FAQ_BLOCKS.has(faqContentItem.block_id ?? '')
        ? makeYearReplacement(selectedPlanYear, FSA_FAQ_HARDCODED_YEAR)
        : undefined;

    return {
      title: faqContentItem.title ?? '',
      content: faqContentItem?.cells?.length ? (
        <FAQTable cells={faqContentItem.cells} />
      ) : (
        <Stack gap={3}>
          <RichText
            inlineParagraphs={false}
            field={`${faqContentItemsPath}[${idx}].content` as TextField}
            vars={vars}
            textReplacements={yearReplacement}
          />
          {faqContentItem.button_text && faqContentItem.button_link && (
            <FAQLinkWrapper>
              <Button onClick={() => window.open(faqContentItem.button_link)} iconRight="ArrowUpRight">
                {faqContentItem.button_text}
              </Button>
            </FAQLinkWrapper>
          )}
        </Stack>
      ),
    };
  });

  return (
    faqItems && (
      <FAQContainer>
        <H3 as="h2">
          <Text field={`health_section.secondary_content.${infoType}_modal.faq_header_text`} />
        </H3>
        <Accordion items={faqItems} chakraAccordionProps={{ allowToggle: true }} />
      </FAQContainer>
    )
  );
};

const FAQTable = ({ cells }) => {
  const headerRow = cells[0];
  const body = cells.slice(1);
  return (
    <TableWrapper>
      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              {headerRow.map((col: string, index) => (
                <Th key={`header:${index}`} role="columnheader">
                  <span>{col}</span>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {body.map((row, index) => (
              <Tr key={index}>
                {row.map((col, i) => {
                  // Row header
                  if (i === 0) {
                    return (
                      <Th scope="row" key={`${index}:${i}`}>
                        <Paragraph size="small">{col}</Paragraph>
                      </Th>
                    );
                  }
                  // Empty Row
                  if (col === '') {
                    return <Td key={`${index}:${i}`}> </Td>;
                  }
                  // Checkmark w/o text
                  if (col === 'X') {
                    return (
                      <Td key={`${index}:${i}`}>
                        <span className="x" aria-label="checkmark">
                          <span aria-hidden>
                            <Icon color="--primary-green" type="CheckCircle" />
                          </span>
                        </span>
                      </Td>
                    );
                  }

                  // Text w/o checkmark for "Tax deferred" column
                  // Needs to be updated if index changes
                  if (i === 2 && index === 1 && row[i]) {
                    return (
                      <Td key={`${index}:${i}`}>
                        <span className="x">
                          <Paragraph size="mini">{col}</Paragraph>
                        </span>
                      </Td>
                    );
                  }

                  // Default: checkmark with text unless equal to tax deferred
                  return (
                    <Td key={`${index}:${i}`}>
                      <span className="x" aria-label="checkmark">
                        <span aria-hidden>
                          <Icon color="--primary-green" type="CheckCircle" />
                        </span>
                        <Paragraph size="mini">{col}</Paragraph>
                      </span>
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </TableWrapper>
  );
};
